import { useMemo, useRef } from "react";
import { ChevronDownIcon, SwapIcon } from "../../icons";
import { formatCryptoValue, formatFiatValue } from "../../utils/format";
import AssetPickerList from "./AssetPickerList";
import { useSelector } from "react-redux";
import { useExchangeRate } from "../../hooks/useExchangeRate";

// const formatFiatSubtext = (value) =>
//   `$${Number(value || 0).toLocaleString("en-US", {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   })}`;

const AssetSelectorButton = ({
  asset,
  placeholder,
  onClick,
  isOpen,
  fullWidth,
  previewAssets,
}) => {
  if (!asset) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`flex items-center justify-between rounded-lg px-3 py-2 custom-transition ${
          fullWidth ? "w-full" : ""
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            {previewAssets?.map((preview, index) => (
              <div
                key={preview.currency_code || index}
                className={`relative size-[31px] shrink-0 overflow-hidden rounded-full ${
                  index < previewAssets.length - 1 ? "-mr-[5.674px]" : ""
                }`}
              >
                <img
                  src={preview.logo}
                  alt={preview.currency_code}
                  className="size-full object-cover"
                />
              </div>
            ))}
          </div>
          <p className="text-sm font-medium leading-[19.5px] text-text-primary">
            {placeholder}
          </p>
        </div>
        <ChevronDownIcon
          className={`shrink-0 text-text-secondary custom-transition ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg px-3 py-2 custom-transition"
    >
      <div
        className="flex size-[27px] shrink-0 items-center justify-start overflow-hidden rounded-full"
        style={asset.iconBg ? { backgroundColor: asset.iconBg } : undefined}
      >
        <img
          src={asset.logo}
          alt={asset.currency_code}
          className="size-[27px] rounded-full object-cover"
        />
      </div>
      <div className="flex flex-col items-start text-left">
        <p className="text-sm font-semibold leading-[19.5px] text-text-primary">
          {asset.title || asset.account_title || asset.currency_code}
        </p>
        <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
          {/* {formatCryptoAmount(asset.balance)} {asset.currency_code} */}
          {formatCryptoValue(asset?.balance, asset?.decimals || 8)}
        </p>
      </div>
      <ChevronDownIcon
        className={`shrink-0 text-text-secondary custom-transition ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
  );
};

const FromAmountInput = ({
  amount,
  symbol,
  fiatValue,
  onAmountChange,
  onMaxClick,
  handleClear
}) => {
  const inputRef = useRef(null);
  const isEmpty = !amount;
  const displayVal = isEmpty ? "0" : amount;

  const amountFontSize = useMemo(() => {
    const len = displayVal.length;
    if (len <= 5) return "text-2xl sm:text-2xl";
    if (len <= 8) return "text-xl sm:text-xl";
    if (len <= 11) return "text-lg sm:text-lg";
    return "text-base sm:text-base";
  }, [displayVal.length]);

  return (
    <div className="flex min-w-0 flex-col items-start">
      <div
        className="flex h-[29px] cursor-text items-center gap-1 min-w-0 max-w-full overflow-hidden"
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        <div className="relative inline-flex min-w-[24px] items-center whitespace-nowrap overflow-hidden">
          <span
            className={`font-medium leading-6 text-text-primary transition-all duration-200 ${amountFontSize}`}
          >
            {displayVal}
          </span>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              const next = e.target.value;
              if (next === "" || /^\d*\.?\d*$/.test(next)) onAmountChange?.(next);
            }}
            className="absolute inset-0 cursor-text opacity-0"
            aria-label="From amount"
          />
        </div>
        <span className="text-2xl font-extralight text-[#4ab933] shrink-0">|</span>
        <p className="text-2xl font-medium tracking-[-1px] text-text-secondary shrink-0">
          {symbol}
        </p>
      </div>

      <div className="flex items-center gap-3 w-full">
        <p className="text-xs font-semibold leading-[21px] text-[#4ab933]">
          ≈ {formatFiatValue(fiatValue)}
        </p>
        <button
          type="button"
          onClick={onMaxClick}
          className="text-xs font-semibold leading-[21px] text-[#4ab933] transition-colors hover:text-[#8ccc7e] active:scale-95"
        >
          Max
        </button>
        {amount && amount !== "0" ? (
            <>
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-medium leading-[21px] text-text-secondary transition-colors hover:text-red-400 active:scale-95"
              >
                Clear
              </button>
            </>
          ) : ""}
      </div>
    </div>
  );
};

const ToAmountDisplay = ({ amount, symbol, decimals = null, fiatValue, isLoading }) => {
  const formattedVal = isLoading ? "...." : formatCryptoValue(amount, decimals) || "0.00";

  const amountFontSize = useMemo(() => {
    const len = formattedVal.toString().length;
    if (len <= 5) return "text-2xl";
    if (len <= 8) return "text-xl";
    if (len <= 11) return "text-lg";
    return "text-base";
  }, [formattedVal]);

  return (
    <div className="flex min-w-0 flex-col items-start">
      <div className="flex h-[29px] items-center gap-2 min-w-0 max-w-full overflow-hidden">
        <p
          className={`font-medium leading-6 text-text-primary transition-all duration-200 truncate ${amountFontSize}`}
        >
          {formattedVal}
        </p>
        <p className="text-2xl font-medium leading-normal tracking-[-1px] text-text-secondary shrink-0">
          {symbol}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <p className="text-xs font-semibold leading-[21px] text-[#4ab933]">
          ≈ {formatFiatValue(fiatValue)}
        </p>
      </div>
    </div>
  );
};

const SwapEntryStep = ({
  fromAsset,
  toAsset,
  fromAmount,
  activePicker,
  onFromAmountChange,
  onOpenPicker,
  onAssetSelect,
  onSwapDirection,
  onContinue,
}) => {
  const cryptoAccounts = useSelector(
    (state) => state.client?.clientInfo?.crypto_accounts || []
  );

  // Direct Exchange Rate between the two selected assets
  const { exchangeRateData, isRateLoading } = useExchangeRate({
    fiatCode: toAsset?.currency_code || "",
    cryptoCode: fromAsset?.currency_code || "",
    isSell: true,
    cryptoDecimal: fromAsset?.decimals
  });

  const pickerAssets = useMemo(
    () =>
      cryptoAccounts.filter((asset) => {
        if (activePicker === "from") return asset.id !== toAsset?.id;
        if (activePicker === "to") return asset.id !== fromAsset?.id;
        return true;
      }),
    [activePicker, fromAsset?.id, toAsset?.id, cryptoAccounts]
  );

  // Determine swap exchange rate
  const exchangeRate = useMemo(() => {
    if (!fromAsset || !toAsset) return 0;

    // Use direct API rate if available
    const apiRate = parseFloat(exchangeRateData?.exchange_rate || exchangeRateData?.unit_price);
    if (apiRate && apiRate > 0) return apiRate;

    // Fallback using USD unit prices if API rate is not yet loaded
    const fromUSD = parseFloat(fromAsset?.unit_price_usd) || 1;
    const toUSD = parseFloat(toAsset?.unit_price_usd) || 1;
    return toUSD > 0 ? fromUSD / toUSD : 0;
  }, [fromAsset, toAsset, exchangeRateData]);

  // Calculate To Amount and Fiat values
  const toAmount = useMemo(() => {
    if (!fromAsset || !toAsset) return 0;
    const fromVal = parseFloat(fromAmount) || 0;
    return fromVal * exchangeRate;
  }, [fromAmount, fromAsset, toAsset, exchangeRate]);

  const fromFiat = (parseFloat(fromAmount) || 0) * (parseFloat(fromAsset?.unit_price_usd) || 0);
  const toFiat = (parseFloat(toAmount) || 0) * (parseFloat(toAsset?.unit_price_usd) || 0);

  const handleMaxClick = () => {
    if (fromAsset?.balance !== undefined && fromAsset?.balance !== null) {
      onFromAmountChange?.(fromAsset.balance.toString());
    }
  };
  
  const handleClear = () => {
    onFromAmountChange?.(0.00);
  };

  const canContinue =
    fromAsset &&
    toAsset &&
    parseFloat(fromAmount) > 0 &&
    fromAsset.id !== toAsset.id &&
    !isRateLoading;

  return (
    <div className="relative flex h-full min-h-0 flex-col font-outfit pt-10">
      <div className="flex flex-1 flex-col justify-between px-6 overflow-y-auto hide-scrollbar">
        <div className="relative flex flex-col gap-[21px] pt-4">
          {/* From Section */}
          <div className="flex flex-col gap-[4px]">
            <p className="text-base font-medium leading-7 text-text-primary">From</p>
            <div className="relative">
              <div className="flex sm:h-[86px] h-[99px] items-center justify-between rounded-[10px] border-[0.5px] border-solid border-[#777] bg-background-surface py-[13px] pl-[22px] pr-6">
                {fromAsset ? (
                  <div className="flex w-full items-center justify-between gap-2">
                    <FromAmountInput
                      amount={fromAmount}
                      symbol={fromAsset?.currency_code}
                      // decimals={toAsset.decimals}
                      fiatValue={fromFiat}
                      onAmountChange={onFromAmountChange}
                      onMaxClick={handleMaxClick}
                      handleClear={handleClear}
                    />
                    <AssetSelectorButton
                      asset={fromAsset}
                      isOpen={activePicker === "from"}
                      onClick={() => onOpenPicker("from")}
                      previewAssets={cryptoAccounts}
                    />
                  </div>
                ) : (
                  <AssetSelectorButton
                    asset={fromAsset}
                    isOpen={activePicker === "from"}
                    placeholder="Select asset to swap"
                    fullWidth
                    onClick={() => onOpenPicker("from")}
                    previewAssets={cryptoAccounts}
                  />
                )}
              </div>
              <AssetPickerList
                assets={pickerAssets}
                onSelect={onAssetSelect}
                selectedAssetId={fromAsset?.id}
                className={`custom-transition ${
                  activePicker === "from" ? "block" : "hidden"
                }`}
              />
            </div>
          </div>

          {/* Swap Direction Divider */}
          <div className="relative z-10 flex justify-center">
            <button
              type="button"
              onClick={onSwapDirection}
              className="flex items-center justify-center rounded-full bg-[rgba(74,185,50,0.1)] custom-transition hover:opacity-80"
              aria-label="Swap direction"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#4ab9321a] text-[#4ab933] bg-surface-secondary">
                  <SwapIcon className={"h-10 w-10 -rotate-90"} />
              </div>
            </button>
          </div>

          {/* To Section */}
          <div className="flex flex-col gap-[4px] -mt-[26px]">
            <p className="text-base font-medium leading-7 text-text-primary">To</p>
            <div className="relative">
              <div className="flex sm:h-[86px] h-[99px] items-center rounded-[10px] border-[0.5px] border-solid border-[#777] bg-background-surface px-6 py-[13px]">
                {toAsset ? (
                  <div className="flex w-full items-center justify-between gap-2">
                    <ToAmountDisplay
                      amount={toAmount}
                      symbol={toAsset.currency_code}
                      decimals={toAsset.decimals}
                      fiatValue={toFiat}
                      isLoading={isRateLoading}
                    />
                    <AssetSelectorButton
                      asset={toAsset}
                      isOpen={activePicker === "to"}
                      fullWidth
                      onClick={() => onOpenPicker("to")}
                    />
                  </div>
                ) : (
                  <AssetSelectorButton
                    placeholder="Select asset to receive"
                    fullWidth
                    onClick={() => onOpenPicker("to")}
                    previewAssets={cryptoAccounts}
                  />
                )}
              </div>
              {activePicker === "to" && (
                <AssetPickerList
                  assets={pickerAssets}
                  onSelect={onAssetSelect}
                  selectedAssetId={toAsset?.id}
                />
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          type="button"
          disabled={!canContinue}
          onClick={onContinue}
          className="mb-6 flex h-12 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white custom-transition enabled:hover:bg-[#3fa82c] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRateLoading ? "Fetching Rate..." : "Continue To Swap"}
        </button>
      </div>
    </div>
  );
};

export default SwapEntryStep;